import { CalendarDay, formatMinutes, getProjectColor } from '@/lib/calendar';
import { Clock } from 'lucide-react';

interface MonthViewProps {
  days: CalendarDay[];
  onDayClick: (date: Date) => void;
}

export function MonthView({ days, onDayClick }: MonthViewProps) {
  const weekDays = ['日', '月', '火', '水', '木', '金', '土'];

  // days が undefined または空の場合のフォールバック
  if (!days || days.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <p className="text-gray-600 text-center">カレンダーを読み込んでいます...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* 曜日ヘッダー */}
      <div className="grid grid-cols-7 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
        {weekDays.map((day, index) => (
          <div
            key={day}
            className={`py-3 text-center font-semibold text-sm ${
              index === 0 ? 'text-red-600' : index === 6 ? 'text-blue-600' : 'text-gray-700'
            }`}
          >
            {day}
          </div>
        ))}
      </div>

      {/* カレンダーグリッド */}
      <div className="grid grid-cols-7">
        {days.map((day, index) => {
          const dayOfWeek = day.date.getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          const records = day.records || [];

          return (
            <button
              key={index}
              onClick={() => onDayClick(day.date)}
              className={`min-h-[120px] p-2 border-b border-r border-gray-100 hover:bg-blue-50 transition-colors duration-150 ${
                !day.isCurrentMonth ? 'bg-gray-50' : ''
              } ${day.isToday ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset' : ''}`}
            >
              <div className="flex flex-col h-full">
                {/* 日付 */}
                <div className="flex items-center justify-between mb-1">
                  <span
                    className={`text-sm font-semibold ${
                      day.isToday
                        ? 'bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center'
                        : !day.isCurrentMonth
                        ? 'text-gray-400'
                        : isWeekend
                        ? dayOfWeek === 0
                          ? 'text-red-600'
                          : 'text-blue-600'
                        : 'text-gray-700'
                    }`}
                  >
                    {day.date.getDate()}
                  </span>
                </div>

                {/* タイムレコード表示 */}
                <div className="flex-grow space-y-1 overflow-y-auto">
                  {records.slice(0, 3).map((record, idx) => {
                    // record が有効かチェック
                    if (!record) return null;
                    
                    return (
                      <div
                        key={idx}
                        className={`text-xs px-2 py-1 rounded ${getProjectColor(
                          record.projectId || record.project?.id
                        )} bg-opacity-20 text-gray-800 truncate`}
                      >
                        {record.notes || record.memo || record.project?.name || 'プロジェクト'}
                      </div>
                    );
                  })}
                  {records.length > 3 && (
                    <div className="text-xs text-gray-500 text-center">
                      +{records.length - 3} 件
                    </div>
                  )}
                </div>

                {/* 合計時間 */}
                {day.totalMinutes > 0 && (
                  <div className="mt-2 flex items-center justify-center gap-1 text-xs font-semibold text-blue-600">
                    <Clock className="w-3 h-3" />
                    <span>{formatMinutes(day.totalMinutes)}</span>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
